import ThaiDatePicker from 'common/components/ThaiDatePicker'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { useDefaultValueLabel } from 'common/utils/defaultValueLabel'
import { getDateFromInputDateTime } from 'common/utils/date'
import endOfToday from 'date-fns/endOfToday'
import { useGetTransactionCategoryList, useGetUnits } from 'modules/cost/api/queries'
import { useFindTransactionById } from 'modules/cost/api/selector'
import { CostCategory, Unit } from 'modules/cost/api/types'
import { useSpeciesName } from 'modules/pond/api/selector'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { normalizeSpeciesName } from 'utils/analytics'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import {
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  FormHelperText,
  Stack,
  CircularProgress,
  useTheme,
  Button,
  Autocomplete,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'

import {
  useMutateCostTransactionCreate,
  useMutateCostTransactionCreateCategory,
  useMutateCostTransactionEditCategory,
  useMutateCostTransactionUpdate,
} from '../../api/mutation'
import CategoryFormDialog from '../CostCategoryDialog'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'

type RecordData = {
  date: string
  time: string
  category: string
  name: string
  quantity: string
  unit: string
  price: string
}

interface Props {
  open: boolean
  onClose: () => void
  initialValues?: RecordData | null
  transactionId?: string | null
  isEdit?: boolean
}

const CostDialogCreateUpdate: React.FC<Props> = ({
  open,
  onClose,
  transactionId = null,
  isEdit = false,
}) => {
  const { t } = useTranslation('cost')
  const label = useDefaultValueLabel()
  const theme = useTheme()
  const [submitting, setSubmitting] = useState(false)
  const initialValues = useFindTransactionById(transactionId ?? '')

  type FormValues = {
    date: Date | null
    time: Date | null
    category: string
    name: string
    quantity: number | string
    unit: string
    price: number | string
  }
  const schema = Yup.object().shape({
    date: Yup.date().required(t('form.date.error', 'Required')),
    time: Yup.date().required(t('form.time.error', 'Required')),
    category: Yup.string().required(t('form.category.error', 'Required')),
    name: Yup.string().required(t('form.name.error', 'Required')),
    quantity: Yup.number()
      .typeError(t('form.quantity.error', 'Required'))
      .required(t('form.quantity.error', 'Required')),
    unit: Yup.string().required(t('form.unit.error', 'Required')),
    price: Yup.number()
      .typeError(t('form.unit.price.error', 'Required'))
      .required(t('form.unit.price.error', 'Required')),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      date: new Date(),
      time: new Date(),
      category: '',
      name: '',
      quantity: '',
      unit: '',
      price: '',
    },
    resolver,
  })

  const {
    data: transactionCategories,
    isLoading: isTransactionCategoriesLoading,
    refetch: refetchTransactionCategories,
  } = useGetTransactionCategoryList()
  const { data: units, isLoading: isUnitsLoading } = useGetUnits()
  const createMutation = useMutateCostTransactionCreate()
  const updateMutation = useMutateCostTransactionUpdate()
  const addCategoryMutation = useMutateCostTransactionCreateCategory()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()
  const editCategoryMutation = useMutateCostTransactionEditCategory()

  const [openCreateCategory, setOpenCreateCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [categoryError, setCategoryError] = useState('')
  const [editingCategory, setEditingCategory] = useState<CostCategory | null>(null)
  const [targetCategory, setTargetCategory] = useState<CostCategory | null>(null);
  const [categoryOpen, setCategoryOpen] = useState(false)

  const combinedCategories = Array.isArray(transactionCategories) ? [...transactionCategories] : []

  const handleOpenCreateCategory = () => {
    setNewCategoryName('')
    setOpenCreateCategory(true)
  }

  const handleCloseCreateCategory = () => {
    setOpenCreateCategory(false)
  }

  const handleConfirmCreateCategory = (name: string) => {
    setCategoryError('')
    const body = { name: name, sequence: combinedCategories.length + 1 }

    addCategoryMutation.mutate(
      { body },
      {
        onSuccess: async () => {
          setNewCategoryName('')
          handleCloseCreateCategory()
          const { data } = await refetchTransactionCategories()
          const newCategory = Array.isArray(data)
            ? data.find((cat: CostCategory) => cat.name === name)
            : undefined
          if (newCategory) {
            setValue('category', String(newCategory.id))
          }
        },
        onError: (error: any) => {
          if (
            error?.response?.status === 400 &&
            error?.response?.data?.message?.includes('Duplicate')
          ) {
            setCategoryError(t('create.category.duplicate.error'))
          } else {
            setCategoryError(t('form.create.category.error'))
          }
        },
      }
    )
  }

  useEffect(() => {
    if (open) {
      if (initialValues && isEdit) {
        const parsedDateTime = initialValues.datetime
          ? new Date(initialValues.datetime.replace(' ', 'T'))
          : null

        const parsedDate = parsedDateTime
          ? new Date(
            parsedDateTime.getFullYear(),
            parsedDateTime.getMonth(),
            parsedDateTime.getDate()
          )
          : new Date()

        let parsedTime = new Date()
        if (parsedDateTime) {
          parsedTime = new Date()
          parsedTime.setHours(parsedDateTime.getHours(), parsedDateTime.getMinutes(), 0, 0)
        }

        reset({
          date: parsedDate,
          time: parsedTime,
          category: initialValues.category?.id ? String(initialValues.category.id) : '',
          name: initialValues.name ?? '',
          quantity: initialValues.quantity ?? '',
          unit: initialValues.unit ?? '',
          price: initialValues.price_per_unit ?? '',
        })
      } else {
        reset({
          date: new Date(),
          time: new Date(),
          category: '',
          name: '',
          quantity: '',
          unit: '',
          price: '',
        })
      }
      setSubmitting(false)
    }
  }, [open, initialValues, reset, isEdit])

  const formatDateTimeLocal = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0')
    const year = d.getFullYear()
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    const hour = pad(d.getHours())
    const minute = pad(d.getMinutes())
    return `${year}-${month}-${day} ${hour}:${minute}:00`
  }

  const onSubmit = (values: FormValues) => {
    if (!values.date || !values.time) return
    setSubmitting(true)

    const dateObj = getDateFromInputDateTime(values.date, values.time)
    const body = {
      date_time: formatDateTimeLocal(dateObj),
      category_id: values.category,
      name: values.name,
      quantity: Number(values.quantity),
      unit: values.unit,
      price_per_unit: Number(values.price),
    }
    const normalizedSpecies = normalizeSpeciesName(speciesName)
    if (transactionId) {
      updateMutation.mutate(
        { transactionId, body },
        {
          onSuccess: () => {
            onClose()
            trackEvent(AnalyticsEvents.costRecordUpdated, {
              cost_category_id: body.category_id,
              ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
            })
          },
        }
      )
    } else {
      createMutation.mutate(
        { body },
        {
          onSuccess: () => {
            onClose()
            trackEvent(AnalyticsEvents.costRecordCreated, {
              cost_category_id: body.category_id,
              ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
            })
          },
        }
      )
    }
  }
  const handleEditCategory = async (name: string) => {
    if (!editingCategory) return

    editCategoryMutation.mutate(
      {
        categoryId: String(editingCategory.id),
        body: {
          name,
          sequence: 1,
        },
      },
      {
        onSuccess: async () => {
          setEditingCategory(null)
          setOpenCreateCategory(false)

          const { data } = await refetchTransactionCategories()

          const updated = Array.isArray(data)
            ? data.find((cat) => cat.id === editingCategory.id)
            : undefined

          if (updated) {
            setValue('category', String(updated.id))
          }
        },
      }
    )
  }
  const { dialog: deleteConfirmDialog, openDialog: openDeleteConfirm } = useDeleteConfirmDialog({
    title: t('form.confirm.delete'),
    content: t('form.delete.title', { categoryName: targetCategory?.name }),
    onConfirm: async () => {
      if (!targetCategory) return;
      return new Promise((resolve, reject) => {
        editCategoryMutation.mutate(
          {
            categoryId: String(targetCategory.id),
            body: {
              name: targetCategory.name,
              sequence: targetCategory.sequence,
              is_hide: true,
            },
          },
          {
            onSuccess: () => {
              refetchTransactionCategories();
              resolve(true)
            },
            onError: (err) => reject(err),
          }
        )
      })
    },
  });
  const handleDeleteCategory = (category: CostCategory) => {
    setTargetCategory(category);
    setTimeout(() => openDeleteConfirm(), 0);
  };

  return (
    <BaseRecordDialog
      onClose={onClose}
      open={open}
      formId="cost-record-form"
      title={t('record.costs')}
      isSubmitting={submitting}
      isShowPondName={false}
    >
      <form id="cost-record-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={6}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <Stack direction="column" spacing={0.5} alignItems="flex-start">
                  <Typography variant="caption" fontSize={16} fontWeight={600}>
                    <Box component="span" color="error.main" sx={{ mr: 0.5 }}>
                      *
                    </Box>
                    {t('form.date')}
                  </Typography>
                  <ThaiDatePicker
                    {...field}
                    value={field.value}
                    onChange={field.onChange}
                    format="dd/MM/yyyy"
                    maxDate={endOfToday()}
                    slotProps={{
                      textField: {
                        id: 'cost-date-picker',
                        size: 'small',
                        error: !!errors.date,
                        helperText: errors.date?.message,
                      },
                    }}
                    sx={{ width: '100%' }}
                  />
                </Stack>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="time"
              control={control}
              render={({ field }) => (
                <Stack direction="column" spacing={0.5} alignItems="flex-start">
                  <Typography variant="caption" fontSize={16} fontWeight={600}>
                    <Box component="span" color="error.main" sx={{ mr: 0.5 }}>
                      *
                    </Box>
                    {t('form.time')}
                  </Typography>
                  <TimePicker
                    {...field}
                    value={field.value}
                    onChange={field.onChange}
                    format="HH:mm"
                    views={['hours', 'minutes']}
                    timeSteps={{ minutes: 1 }}
                    ampm={false}
                    slotProps={{
                      textField: {
                        id: 'cost-time-picker',
                        size: 'small',
                        error: !!errors.time,
                        helperText: errors.time?.message,
                      },
                    }}
                    sx={{ width: '100%' }}
                  />
                </Stack>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.category}>
              <Typography variant="caption" sx={{ mb: 0.5 }} fontSize={16} fontWeight={600}>
                <Box component="span" color="error.main" sx={{ mr: 0.5 }}>
                  *
                </Box>
                {t('form.category')}
              </Typography>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={combinedCategories || []}
                    loading={isTransactionCategoriesLoading}
                    getOptionLabel={(option) => label.costCategory(option?.name)}
                    value={
                      combinedCategories.find(
                        (c) => String(c.id) === field.value
                      ) || null
                    }
                    onChange={(_, value) => {
                      field.onChange(value ? String(value.id) : '')
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('form.category.placeholder')}
                        size="small"
                        error={!!errors.category}
                        helperText={errors.category?.message}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center',
                          }}
                        >
                          <Typography>{label.costCategory(option.name)}</Typography>

                          {!option.is_default && (
                            <Box>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setEditingCategory(option)
                                  setNewCategoryName(option.name)
                                  setOpenCreateCategory(true)
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteCategory(option)
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </li>
                    )}
                  />
                )}
              />

              {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
            </FormControl>
            <Button
              startIcon={<AddIcon fontSize="small" />}
              onClick={handleOpenCreateCategory}
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                '&:hover': { backgroundColor: 'transparent', opacity: 0.8 },
                mt: 1,
              }}
            > {t('create.new.category')}</Button>
          </Grid>
          <Grid item xs={6}>
            <Stack direction="column" spacing={0.5}>
              <Typography variant="caption" fontSize={16} fontWeight={600}>
                <Box component="span" color="error.main" sx={{ mr: 0.5 }}>
                  *
                </Box>
                {t('form.name')}
              </Typography>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder={t('form.name.placeholder')}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    size="small"
                  />
                )}
              />
            </Stack>
          </Grid>

          <Grid item xs={6}>
            <Stack direction="column" spacing={0.5}>
              <Typography variant="caption" fontSize={16} fontWeight={600}>
                <Box component="span" color="error.main" sx={{ mr: 0.5 }}>
                  *
                </Box>
                {t('form.quantity')}
              </Typography>
              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder={t('form.quantity.placeholder')}
                    fullWidth
                    error={!!errors.quantity}
                    helperText={errors.quantity?.message}
                    size="small"
                    type="number"
                  />
                )}
              />
            </Stack>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth error={!!errors.unit}>
              <Typography variant="caption" sx={{ mb: 0.5 }} fontSize={16} fontWeight={600}>
                <Box component="span" color="error.main" sx={{ mr: 0.5 }}>
                  *
                </Box>
                {t('form.unit')}
              </Typography>
              <Controller
                name="unit"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    onChange={field.onChange}
                    displayEmpty
                    size="small"
                    disabled={isUnitsLoading}
                  >
                    {isUnitsLoading && (
                      <MenuItem value="" disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {t('form.loading')}
                      </MenuItem>
                    )}

                    {!isUnitsLoading && (
                      <MenuItem value="">{t('select.unit')}</MenuItem>
                    )}

                    {!isUnitsLoading &&
                      Array.isArray(units) &&
                      units.map((unit: Unit) => (
                        <MenuItem key={unit.id} value={unit.name}>
                          {label.unit(unit.name)}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
              {errors.unit && <FormHelperText>{errors.unit.message}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <Stack direction="column" spacing={0.5}>
              <Typography variant="caption" fontSize={16} fontWeight={600}>
                <Box component="span" color="error.main" sx={{ mr: 0.5 }}>
                  *
                </Box>
                {t('form.unit.price')}
              </Typography>
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder={t('form.unit.price.placeholder')}
                    fullWidth
                    error={!!errors.price}
                    helperText={errors.price?.message}
                    size="small"
                    type="number"
                  />
                )}
              />
            </Stack>
          </Grid>
        </Grid>
      </form>
      <CategoryFormDialog
        open={openCreateCategory}
        onClose={() => {
          setOpenCreateCategory(false)
          setEditingCategory(null)
          setCategoryError('')
        }}
        initialName={newCategoryName}
        isEdit={!!editingCategory}
        onConfirm={(name) => {
          setNewCategoryName(name)

          if (editingCategory) {
            handleEditCategory(name)
          } else {
            handleConfirmCreateCategory(name)
          }
        }}
      />
      {deleteConfirmDialog}
    </BaseRecordDialog>
  )
}

export default CostDialogCreateUpdate
